import React, { FC, useCallback, useMemo } from "react";
import { BarChartData } from "@evergis/charts";

import { BarChartContainer, BarChartWrapper, StyledBarChart } from "./styled";
import { Legend } from "./Legend";

import { useRenderBarTooltip } from "./hooks/useRenderBarTooltip";
import { useCustomYAxisLeft } from "./hooks/useCustomYAxisLeft";
import { getGradientColors } from "./utils/getGradientColors";

import { ChatChartProps } from "./types";

export const BarChart: FC<ChatChartProps> = ({ chartData }) => {
  const renderTooltip = useRenderBarTooltip();

  const gradientColors = useMemo(() => {
    return getGradientColors(chartData.labels.length);
  }, [chartData]);

  const data = useMemo(() => {
    return chartData.labels.map(
      (label, labelIndex) =>
        ({
          groupName: label,
          ...Array.from(Array(chartData.datasets.length), (_, index) => index).reduce(
            (acc, curr) => ({
              ...acc,
              [label]: {
                [`value${labelIndex}`]: chartData.datasets[curr].data[labelIndex],
              },
            }),
            {},
          ),
        } as BarChartData),
    );
  }, [chartData]);

  const colors = useMemo(() => {
    return chartData.labels.reduce(
      (acc: Record<string, string>, curr, currIndex) => ({
        ...acc,
        [`value${currIndex}`]: gradientColors[currIndex],
      }),
      {},
    );
  }, [chartData, gradientColors]);

  const legend = useMemo(() => {
    return chartData.labels.map((label, labelIndex) => ({
      name: label,
      color: colors[`value${labelIndex}`],
    }));
  }, [chartData, colors]);

  const customXAxis = useCallback(xAxis => {
    xAxis.remove();
  }, []);

  const customYAxisLeft = useCustomYAxisLeft();

  const customBars = useCallback(({ bars }) => {
    bars.attr("rx", 2).attr("ry", 2);
  }, []);

  return (
    <BarChartWrapper>
      <BarChartContainer>
        <StyledBarChart
          data={data}
          colors={colors}
          customBars={customBars}
          customXAxis={customXAxis}
          customYAxisLeft={customYAxisLeft}
          xScaleItemWidth={80}
          drawGridX
          dynamicTooltipEnable
          renderTooltip={renderTooltip}
          height={250}
          margin={{
            top: 10,
            right: 10,
            bottom: 10,
            left: 10,
          }}
        />
      </BarChartContainer>
      <Legend direction="row" gap="0.75rem" justify="center" toggleAlign="center" items={legend} />
    </BarChartWrapper>
  );
};
